/**
 * @file   src\components\AgentDashboard\AssignClientModal.js
 * @brief  Quick-add / assign a client to an existing want-listing, for use
 *         wherever the dashboard shows the Client column as N/A.
 */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { toast } from "react-toastify";
import searchIccon from "../../images/search-icon.svg";
import { getAgentsList } from "../../services/wantAdService";
import { saveAgentClient } from "../../services/agentClientsService";
import { setWantAdClient } from "../../services/v2/wantAdService";
import { renameKey, combineKey } from "../../helpers/common";
import { extractApiErrorMessage } from "../../helpers/wantAdClient";
import AddNewClientModal from "../MemberWantAd/AddNewClientModal";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <img src={searchIccon} />
    </components.DropdownIndicator>
  );
};

const AssignClientModal = ({ show, onHide, wantAdId, onSaved }) => {
  const [clientsList, setClientsList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      getClientList();
      setSelectedClient(null);
    }
  }, [show]);

  const getClientList = async () => {
    try {
      const response = await getAgentsList();
      const result = _.get(response, "result", null);
      if (response.errorCode === 0 && result) {
        result.forEach((obj) => renameKey(obj, "ClientId", "value"));
        result.forEach((obj) => combineKey(obj, "FirstName", "LastName", "label"));
        setClientsList(result);
      }
    } catch (err) {
      console.log("Error", err);
    }
  };

  const handleChange = (option) => {
    if (!option || option.value == 0 || option.__isNew__) {
      setShowAddClient(true);
    } else {
      setSelectedClient(option);
    }
  };

  const addClientData = async (data) => {
    try {
      const response = await saveAgentClient(data);
      if (response.errorCode === 0) {
        if (response?.result?.agentExist) {
          // No clientId comes back for a duplicate email - fall back to the
          // select dropdown so the agent can search for the existing client
          // instead of getting stuck on an unsubmittable add-client form.
          toast.error(response.message);
          getClientList();
          setShowAddClient(false);
          return;
        }
        getClientList();
        setSelectedClient({
          value: response.result.clientId,
          label: `${data.firstName} ${data.lastName}`.trim(),
        });
        setShowAddClient(false);
      }
    } catch (err) {
      console.log("Error", err);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onHide && onHide();
  };

  const handleConfirm = async () => {
    if (!selectedClient?.value) return;
    setSubmitting(true);
    try {
      await setWantAdClient(wantAdId, { clientId: selectedClient.value });
      toast.success("Client assigned.");
      onSaved && onSaved();
      onHide && onHide();
    } catch (err) {
      toast.error(
        extractApiErrorMessage(err, "Could not assign client. Try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="md">
        <Modal.Body>
          <h3 className="text-start">Assign a client</h3>
          <p className="f-size-16 mb-4">
            Select an existing client or create a new one to organize this
            Want-Listing.
          </p>
          <CreatableSelect
            components={{ DropdownIndicator }}
            value={selectedClient}
            options={clientsList}
            className="basic-multi-select mb-4"
            classNamePrefix="select"
            placeholder="Client name"
            onChange={handleChange}
            formatCreateLabel={() => "Create new client"}
            createOptionPosition="first"
            isValidNewOption={() => true}
          />
          <div className="d-flex justify-content-end">
            <Button variant="link" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={submitting || !selectedClient?.value}
            >
              {submitting ? "Saving..." : "Assign client"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <AddNewClientModal
        show={showAddClient}
        onHide={() => setShowAddClient(false)}
        addClient={addClientData}
      />
    </>
  );
};

export default AssignClientModal;
